import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Product as V } from '../components/DashboardLayout';
import { usePlatformConfig } from '../context/PlatformConfigContext';
import { getDefaultMinimumTransactionAmount, getPayoutScheduleMessage } from '../utils/payoutSchedule';

const buildFaqs = (payoutSchedule, minimumTransactionAmount, platformFeePercent) => [
  {
    category: 'HOW IT WORKS',
    items: [
      {
        q: 'How does TrustTrade protect payments?',
        a: `Payments are processed and securely held by our third-party payment provider until the transaction conditions are met. When you create a transaction on TrustTrade, the buyer pays securely. The seller delivers or hands over as agreed. Once the buyer confirms, funds are released. ${payoutSchedule.copy} Paid is shown only after bank settlement.`,
      },
      {
        q: 'How do I start a transaction?',
        a: 'Log in and click "New Transaction". Enter the item description, price, and add the other party\'s email or TrustTrade username. Share the transaction link with your buyer or seller. The buyer pays via Card or Ozow, the payment is protected, and confirmation releases funds.',
      },
      {
        q: 'What is a Milestone Deal?',
        a: 'A Milestone Deal lets you agree on work and pay for it in stages. The client funds each stage through the payment provider, and the payment is released when that stage is approved. You can also choose a single payment for the whole job.',
      },
      {
        q: 'Can I cancel a transaction?',
        a: 'Transactions can be cancelled before payment is made. Once payment is protected, cancellation requires mutual agreement or a dispute resolution. If the buyer paid but the seller never delivered, the buyer can raise a dispute and funds will be refunded after investigation.',
      },
    ],
  },
  {
    category: 'FEES & PAYMENTS',
    items: [
      {
        q: 'What are TrustTrade\'s fees?',
        a: `TrustTrade fee: ${platformFeePercent}% of the item price (minimum R5). The full breakdown, including the item price, TrustTrade fee, TradeSafe’s processing fee, VAT and total, is shown on the payment screen before you pay. Minimum transaction amount is R${minimumTransactionAmount.toFixed(0)} to keep payout economics sustainable.`,
      },
      {
        q: 'Who pays the fees — the buyer or the seller?',
        a: 'When you create a transaction, you choose who covers the fees. Buyer Pays: the buyer covers the fees, so the seller receives their full asking price. Seller Pays: the fees are deducted from the seller’s payout instead, and the buyer pays only the item price. Split 50/50: the fees are shared evenly between buyer and seller. Payment processing fees depend on the payment method. The full breakdown, including TradeSafe’s processing fee and VAT, is shown on the payment screen before you pay.',
      },
      {
        q: 'Which payment methods are accepted?',
        a: 'Buyers can pay via credit/debit card or Ozow Instant EFT. The full breakdown, including TradeSafe’s processing fee and VAT, is shown on the payment screen before you pay. Check for Payment Protected before handing over the item.',
      },
      {
        q: 'How long does it take to receive my payout?',
        a: payoutSchedule.copy,
      },
      {
        q: 'Which banks are supported for payouts?',
        a: 'All major South African banks are supported: ABSA, Standard Bank, FNB, Nedbank, Capitec, Discovery Bank, TymeBank, African Bank, Bidvest Bank, and Investec Bank. Your banking details must be verified before your first payout.',
      },
    ],
  },
  {
    category: 'SECURITY & VERIFICATION',
    items: [
      {
        q: 'Is my money safe?',
        a: 'Yes. Payments are processed and securely held by our third-party payment provider until the transaction conditions are met. Your money is never held directly by TrustTrade — it can only be released with your confirmation or a dispute resolution.',
      },
      {
        q: 'Why do I need to verify my identity?',
        a: 'Identity verification is required by South African FICA (Financial Intelligence Centre Act) regulations for platforms handling financial transactions. Verification helps us prevent fraud and money laundering, and protects all users on the platform.',
      },
      {
        q: 'Why do I need to add a phone number?',
        a: 'Your phone number is used for transaction alerts and two-factor security confirmations. It also helps verify your identity as required by FICA. You can add your phone number under Settings → Verify Phone.',
      },
      {
        q: 'How is my personal data protected?',
        a: 'TrustTrade complies with the Protection of Personal Information Act (POPIA). Your data is encrypted, stored securely, and never sold to third parties. You can request access to or deletion of your data at any time. See our Privacy Policy for full details.',
      },
    ],
  },
  {
    category: 'DISPUTES',
    items: [
      {
        q: 'What happens if the item is not delivered?',
        a: 'If you\'re the buyer and the item was not delivered, do not confirm receipt. Instead, open a dispute from the transaction page. Upload evidence. TrustTrade will investigate and, if the claim is valid, refund the protected payment to you. Unresolved cases may be escalated to our payment provider.',
      },
      {
        q: 'What happens if the item is not as described?',
        a: 'Raise a dispute before confirming delivery. Include evidence such as photos and a description of the discrepancy. TrustTrade will review the evidence from both parties and make a determination — see "How long does dispute resolution take?" below for typical timelines.',
      },
      {
        q: 'How long does dispute resolution take?',
        a: 'Simple disputes are typically resolved within 3–5 business days. Complex disputes requiring more evidence may take up to 10 business days. You\'ll be notified by email at each stage of the process.',
      },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderBottom: `1px solid ${V.border}`,
    }}
    >
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 20px', gap: 12, width: '100%', background: 'transparent', border: 0, textAlign: 'left', cursor: 'pointer',
      }}>
        <span style={{ fontSize: 14, color: V.text, fontWeight: 500, flex: 1 }}>{q}</span>
        {open
          ? <ChevronUp size={14} color={V.sub} style={{ flexShrink: 0 }} />
          : <ChevronDown size={14} color={V.sub} style={{ flexShrink: 0 }} />
        }
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px', fontSize: 13, color: V.sub, lineHeight: 1.65 }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  const { config: platformConfig } = usePlatformConfig();
  const minimumTransactionAmount = getDefaultMinimumTransactionAmount(platformConfig);
  const payoutSchedule = getPayoutScheduleMessage(new Date(), platformConfig);
  const platformFeePercent = platformConfig.platform_fee_percent ?? 2;
  const FAQS = buildFaqs(payoutSchedule, minimumTransactionAmount, platformFeePercent);

  return (
    <div style={{ minHeight: '100vh', background: V.bg, color: V.text, fontFamily: V.sans }}>
      {/* Top bar */}
      <div style={{
        borderBottom: `1px solid ${V.border}`,
        padding: '14px 24px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none',
            color: V.sub, fontSize: 12, cursor: 'pointer',
            fontFamily: V.mono, letterSpacing: '0.06em',
          }}
        >
          <ArrowLeft size={12} /> BACK
        </button>
        <div style={{ width: 1, height: 16, background: V.border }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={14} color={V.accent} />
          <span style={{ fontSize: 12, color: V.sub, fontFamily: V.mono, letterSpacing: '0.08em' }}>
            TRUSTTRADE
          </span>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 32px' }}>
        <p style={{ fontSize: 10, color: V.accent, fontFamily: V.mono, letterSpacing: '0.2em', margin: '0 0 12px' }}>
          HELP CENTER
        </p>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: V.text, margin: '0 0 12px', letterSpacing: '-0.02em' }}>
          Frequently Asked Questions
        </h1>
        <p style={{ fontSize: 15, color: V.sub, margin: 0, lineHeight: 1.6 }}>
          Everything you need to know about protected payments with TrustTrade.
        </p>
      </div>

      {/* FAQ sections */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px 64px', display: 'flex', flexDirection: 'column', gap: 32 }}>
        {FAQS.map(section => (
          <div key={section.category}>
            <p style={{
              fontSize: 10, fontWeight: 700, color: V.sub,
              fontFamily: V.mono, letterSpacing: '0.15em',
              margin: '0 0 12px',
            }}>
              {section.category}
            </p>
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${V.border}`,
              borderRadius: 4, overflow: 'hidden',
            }}>
              {section.items.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        ))}

        {/* Still need help */}
        <div style={{
          background: 'rgba(0,209,255,0.04)',
          border: `1px solid rgba(0,209,255,0.2)`,
          borderRadius: 4, padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap',
        }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, color: V.text, margin: '0 0 4px' }}>
              Still need help?
            </p>
            <p style={{ fontSize: 13, color: V.sub, margin: 0 }}>
              Email us at{' '}
              <a href="mailto:trusttrade.register@gmail.com" style={{ color: V.accent, textDecoration: 'none' }}>
                trusttrade.register@gmail.com
              </a>
            </p>
          </div>
          <a
            href="mailto:trusttrade.register@gmail.com"
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '8px 18px', borderRadius: 3,
              border: `1px solid rgba(0,209,255,0.3)`,
              background: 'rgba(0,209,255,0.08)',
              color: V.accent, fontSize: 13, fontWeight: 600,
              textDecoration: 'none', fontFamily: V.sans,
            }}
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
